//import React, { useRef, useState } from 'react';
import Logger from '../logger/logger';
import { cascadePosition } from '../geometry/placement';
import { objectCenter } from '../geometry/fabricRegion';

// The filters the parent can ask for, in the vocabulary it asks for them in, and
// how to recognise each one on an image that already carries it.
//
// One table, read in both directions: editImage builds from it and describeSelected
// reports from it. Two hand-written mappings would drift, and the parent would end
// up sending "cold" and being told "BlendColor" - which is not even enough to tell
// it apart from "red", since six of these are a BlendColor and differ only in
// colour. That is what `options` is for: the settings that identify an entry.
// `value` names the one setting the parent supplies, and is the one reported back.
export const IMAGE_FILTERS = [
	{ name: 'grayscale',   filter: 'Grayscale' },
	{ name: 'sepia',       filter: 'Sepia' },
	{ name: 'invert',      filter: 'Invert' },
	{ name: 'black_white', filter: 'BlackWhite' },
	{ name: 'vintage',     filter: 'Vintage' },
	{ name: 'brightness',  filter: 'Brightness', value: 'brightness' },
	{ name: 'contrast',    filter: 'Contrast',   value: 'contrast' },
	// fabric's setting is `distance`, not `threshold`.
	{ name: 'remove_white', filter: 'RemoveColor', value: 'distance', options: { color: '#fff' } },
	// `alpha` is how hard the blend hits - fabric's own strength setting. It used to
	// be written into the colour as an rgba channel, where fabric never read it:
	// applyTo2d multiplies by this.alpha and the WebGL path overwrites the parsed
	// source[3] with it. Every blend ran at full force. Turn these to taste.
	{ name: 'cold',   filter: 'BlendColor', options: { color: 'rgb(0, 0, 255)',     mode: 'add', alpha: 0.2 } },
	{ name: 'milk',   filter: 'BlendColor', options: { color: 'rgb(255, 255, 255)', mode: 'add', alpha: 0.3 } },
	{ name: 'purple', filter: 'BlendColor', options: { color: 'rgb(128, 0, 128)',   mode: 'add', alpha: 0.2 } },
	{ name: 'yellow', filter: 'BlendColor', options: { color: 'rgb(255, 255, 0)',   mode: 'add', alpha: 0.2 } },
	{ name: 'red',    filter: 'BlendColor', options: { color: 'rgb(255, 0, 0)',     mode: 'add', alpha: 0.2 } },
	{ name: 'green',  filter: 'BlendColor', options: { color: 'rgb(0, 255, 0)',     mode: 'add', alpha: 0.2 } },
	{ name: 'monochrome', filter: 'ColorMatrix', options: { matrix: [
		0.3, 0.3, 0.3, 0, 0,
		0.3, 0.3, 0.3, 0, 0,
		0.3, 0.3, 0.3, 0, 0,
		0,   0,   0,   1, 0
	] } }
];

// Colours are compared by their rgb, not as text. Two reasons, and both are about
// the same colour being written more than one way: a blend stored before the
// strength moved out of the string carries `rgba(0, 255, 0, 0.2)` where the table
// now says `rgb(0, 255, 0)`, and it is the same blend either way; and fabric is
// free to hand back a colour in a form it was not given in.
export function colorKey(color) {
	if (typeof color !== 'string') return color;

	const rgb = color.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*[\d.]+\s*)?\)$/i);
	if (rgb) return `${+rgb[1]},${+rgb[2]},${+rgb[3]}`;

	const hex = color.trim().toLowerCase().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
	if (hex) {
		const full = hex[1].length === 3 ? hex[1].replace(/./g, c => c + c) : hex[1];
		return [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16)).join(',');
	}

	// A named colour, or something this does not parse: compare it as it stands.
	return color.trim().toLowerCase();
}

function sameSetting(key, a, b) {
	if (key === 'color') return colorKey(a) === colorKey(b);
	if (Array.isArray(a) || Array.isArray(b)) {
		return Array.isArray(a) && Array.isArray(b) &&
			a.length === b.length && a.every((v, i) => v === b[i]);
	}
	return a === b;
}

// Every option identifies an entry except `alpha`: designs stored before the
// strength was set carry alpha 1 whatever blend they are, and they still have to
// come back under their own name. No two entries differ by strength alone - there
// is a test holding that true, because this is what would break if one ever did.
function filterMatches(entry, filter) {
	if (!filter || filter.type !== entry.filter) return false;
	const options = entry.options || {};
	return Object.keys(options)
		.filter(key => key !== 'alpha')
		.every(key => sameSetting(key, filter[key], options[key]));
}

// Build the fabric filter one entry of the parent's list asks for, or null when it
// names something this table - or this fabric - does not have.
export function createFilter(name, value) {
	const entry = IMAGE_FILTERS.find(e => e.name === name);
	if (!entry) return null;

	const available = window.fabric && window.fabric.Image && window.fabric.Image.filters;
	const Filter = available && available[entry.filter];
	if (!Filter) {
		console.warn('ImageTools: this fabric has no', entry.filter, 'filter');
		return null;
	}

	const options = { ...(entry.options || {}) };
	// Left at fabric's own default when the parent sends no value, rather than
	// handed an explicit undefined.
	if (entry.value && value != null) options[entry.value] = value;

	return new Filter(options);
}

// What the parent is told about one filter on the image: its own name for it and
// the setting it supplied, so a described list can go straight back into editImage.
export function describeFilter(filter) {
	const entry = IMAGE_FILTERS.find(e => filterMatches(e, filter));
	// Not one of ours - report fabric's own name rather than dropping it silently.
	if (!entry) return { type: filter && filter.type };

	const described = { type: entry.name };
	if (entry.value) described.value = filter[entry.value];
	return described;
}

// fabric's applyFilters picks how to draw by comparing the element the image is
// drawn from with the original: the same one means "make a canvas to filter into",
// a different one means "the filtered canvas is in _filteredEl, clear and reuse
// it". The resize filter (see renderQuality) leaves a third state neither branch
// expects - applyResizeFilters swaps _element for its own resampled canvas and
// never touches _filteredEl - so any image drawn below 1:1, which is nearly all of
// them, takes the reuse branch and reads getContext off nothing:
//
//   Uncaught TypeError: Cannot read properties of undefined (reading 'getContext')
//
// Putting the original back is exactly what fabric leaves behind when a filter
// list is emptied, and applyFilters then builds its own canvas from scratch. The
// scale is cleared with it so the next render resamples the filtered result -
// without that, _needsResize sees the scale it already resized at and the freshly
// filtered canvas is drawn at preview size unresampled, which is the softness
// renderQuality is there to prevent.
export function resetFilterElement(obj){
	if (!obj || !obj._originalElement) return;
	if (obj._filteredEl || obj._element === obj._originalElement) return;

	obj._element = obj._originalElement;
	obj._filterScalingX = 1;
	obj._filterScalingY = 1;
	obj._lastScaleX = 1;
	obj._lastScaleY = 1;
}

export default class ImageTools{
	constructor({canvasRef, handleSave, boundingBoxes}){
		this.canvasRef = canvasRef;
		this.handleSave = handleSave;
		this.boundingBoxes = boundingBoxes;
		this.selectedObject = null;
		// The image addImage just put on the canvas, waiting for the selection it
		// triggers. Cleared as soon as that selection lands - see setSelectedObject.
		this.pendingNotify = null;
	}

	// Selecting an image no longer tells the parent about it: picking an element up
	// should only select it, and the buttons on the element toolbar are what ask
	// for a panel.
	//
	// One exception, and it is a single shot: the selection that follows a fresh
	// upload does tell the parent, so its panel opens on the new image without the
	// customer having to press a button for the thing they just added. Selecting
	// that same image again later only selects it.
	setSelectedObject(obj){
		this.selectedObject = obj;
		//const justAdded = obj != null && obj === this.pendingNotify;
		//if (justAdded) {
			//this.pendingNotify = null;
			this.postSelected();
		//}
	}

	// What the parent is told about the image. Its panels key off the src, so every
	// message about the selection carries the same description.
	describeSelected(){
		const obj = this.selectedObject;
		if (!obj) return null;
		return {
			type: obj.type,
			src: obj.type === 'image' ? this.currentSrc() : null,
			// Named `filter`, and shaped, exactly as editImage takes it: what comes
			// out of here can be handed straight back with one entry changed.
			filter: this.describeFilters(),
			left: obj.left,
			top: obj.top,
			scaleX: obj.scaleX,
			scaleY: obj.scaleY,
			angle: obj.angle
		};
	}

	// The filters currently on the image, in the order they are applied. An SVG is
	// a group with no filter pipeline at all, so it reports an empty list rather
	// than nothing - the parent's panel has the same shape to read either way.
	describeFilters(){
		const obj = this.selectedObject;
		if (!obj || !Array.isArray(obj.filters)) return [];
		return obj.filters.filter(Boolean).map(describeFilter);
	}

	// The image that was just added. Sent once, from the selection the upload
	// triggers - never again for the same image.
	postSelected(){
		const value = this.describeSelected();
		if (!value) return;
		window.parent.postMessage({ action: 'imageSelected', value }, '*');
	}

	// "Afbeelding aanpassen" - opens the parent's image panel. A separate message
	// from the one above: that one reports a new image landing on the canvas, this
	// one is the customer asking for the panel on an image already there.
	postAdvanced(){
		const value = this.describeSelected();
		if (!value) return;
		window.parent.postMessage({ action: 'imageAdvanced', value }, '*');
	}

	// "Achtergrond verwijderen" - same description, different panel.
	//
	// `button` is where the button that was pressed sits, so the parent can put its
	// panel next to it rather than in a fixed corner. Left out when the caller has
	// no button to point at - a keyboard shortcut, or a test.
	postRemoveBackground(button){
		const value = this.describeSelected();
		if (!value) return;
		if (button) value.button = { left: button.left, top: button.top };
		window.parent.postMessage({ action: 'showRemoveBackground', value }, '*');
	}

	// Place a freshly added image the way a freshly added text is placed: scaled
	// down to fit (never enlarged) and centred - in the box it is being inserted
	// into, in the default box when there is no active one, otherwise on the
	// canvas - then nudged clear of whatever is already sitting on that centre.
	placeNewImage(img, boundingBox){
		const canvas = this.canvasRef.current;
		const box = boundingBox
			|| (this.boundingBoxes && this.boundingBoxes.length > 0
				? (this.boundingBoxes.find(b => b.default === true) || this.boundingBoxes[0])
				: null);

		// The rectangle to centre in, and the largest the image may be inside it.
		const area = box
			? { left: box.left, top: box.top, width: box.width, height: box.height }
			: { left: 0, top: 0, width: canvas.width, height: canvas.height };
		const maxWidth = box ? area.width : canvas.width * 0.8;
		const maxHeight = box ? area.height : canvas.height * 0.8;

		const scale = Math.min(1, maxWidth / img.width, maxHeight / img.height);
		img.set({ scaleX: scale, scaleY: scale });

		// Read the size back after scaling so the centring matches what is drawn.
		const width = img.getScaledWidth();
		const height = img.getScaledHeight();

		// A second upload dropped on the exact centre of the box disappears behind
		// the first one, with no sign there are two. Step it off, same as a clone
		// and same as a second text.
		const taken = (canvas && canvas.getObjects ? canvas.getObjects() : [])
			.filter(o => o !== img && (o.type === 'text' || o.type === 'image'))
			.map(objectCenter);

		const center = cascadePosition({
			center: { x: area.left + area.width / 2, y: area.top + area.height / 2 },
			size: { width, height },
			bounds: box ? area : null,
			taken
		});

		img.set({ left: center.x - width / 2, top: center.y - height / 2 });
	}

	addImage(data, boundingBox){
		const self = this;
		const canvas = this.canvasRef.current;
		const url = data.url;
		if(url.indexOf('.svg') !== -1){
			// Fetch SVG text and use loadSVGFromString to avoid potential tainting when loading from URL
			fetch(url).then(res => res.text()).then(svgText => {
				window.fabric.loadSVGFromString(svgText, (objects, options) => {
					const img = window.fabric.util.groupSVGElements(objects, options);
					// groupSVGElements hands back a group (or a path for a single
					// shape), not an Image. Everything downstream - serialisation,
					// the image/text counters, the tool bindings - keys off
					// type === 'image', so tag it as one and keep the url loadSide
					// needs to rebuild it.
					img.set({ type: 'image', src: url });
					self.placeNewImage(img, boundingBox);
					canvas.add(img);

					// Claim the selection below as the one that reports to the parent.
					self.pendingNotify = img;

					// Defer selection/render to avoid DOM mutation races with React
					setTimeout(() => {
						try {
							canvas.setActiveObject(img);
							if (canvas.requestRenderAll){
								canvas.requestRenderAll();
							}else{
								canvas.renderAll();
							}
						} catch (e) {
							console.warn('ImageTools.addImage: could not set active object immediately', e);
						}
					}, 0);
					
					self.selectedObject = img;
					try{
						self.handleSave();
					}catch(e){
						
					}
					
					try{
						Logger('Image added');
					}catch(e){
						
					}
				});
			}).catch(err => {
				console.warn('Failed to fetch SVG for addImage:', err);
			});
			return;
		}
		window.fabric.Image.fromURL(url, img => {
			self.placeNewImage(img, boundingBox);
			canvas.add(img);

			// Claim the selection below as the one that reports to the parent.
			self.pendingNotify = img;

			// Defer selection/render to avoid DOM mutation races with React
			setTimeout(() => {
				try {
					canvas.setActiveObject(img);
					if (canvas.requestRenderAll){
						canvas.requestRenderAll();
					}else{
						canvas.renderAll();
					}
				} catch (e) {
					console.warn('ImageTools.addImage: could not set active object immediately', e);
				}
			}, 0);
			
			self.selectedObject = img;
		
			setTimeout(() => {
				try { this.handleSave && this.handleSave(); } catch (e) {}
			}, 0);
			
			try{
				Logger('Image added');
			}catch(e){
				
			}
		}, { crossOrigin: 'anonymous' });
	}
	
	editImage(data){
		console.log('editImage', data);
		if (!this.selectedObject || this.selectedObject.type !== 'image') return false;

		// A url replaces the picture itself - the background-removed version of
		// what is on the canvas, say - rather than restyling the one already there.
		// The swap goes first and the rest of the edit is applied to the result:
		// the filters below belong to the new image as much as to the old one, and
		// loading is asynchronous, so applying them now would apply them to the
		// picture that is on its way out.
		//
		// Skipped when the url is the one already on the canvas, so a parent that
		// includes it with every edit does not reload the image on each keystroke.
		if (data.url && data.url !== this.currentSrc()) {
			return this.replaceImageSource(data.url, () => this.applyImageEdit(data));
		}

		return this.applyImageEdit(data);
	}

	// Swap the picture behind the element, keeping it where and how big it is on
	// the shirt.
	replaceImageSource(url, done){
		const obj = this.selectedObject;
		if (typeof obj.setElement !== 'function') {
			// An SVG sits on the canvas as a group, not an Image: there is no
			// element to swap. Say so rather than failing silently.
			console.warn('ImageTools.editImage: cannot replace the source of an SVG element', url);
			return false;
		}

		// The footprint on the shirt, not the raw scale: the new file need not
		// have the dimensions of the old one - a background removal can come back
		// cropped - and a scale carried straight over would resize the element by
		// however much the picture changed size.
		const width = obj.getScaledWidth();
		const height = obj.getScaledHeight();
		const center = obj.getCenterPoint();

		// Loaded before it is swapped in. fabric's own setSrc hands its callback a
		// null element when the load fails and installs it anyway, which leaves a
		// zero-sized, invisible element on the canvas; this way a failed load
		// leaves the picture that is there alone.
		window.fabric.util.loadImage(url, (element, isError) => {
			if (isError || !element) {
				console.warn('ImageTools.editImage: could not load', url);
				return;
			}

			// setElement resets width/height to the new picture's own, and
			// re-applies whatever filters the element already carried.
			obj.setElement(element);

			// Fitted inside the old footprint at its own aspect ratio rather than
			// stretched to fill it: same size when the dimensions did not change,
			// never distorted when they did. It can only stay the same or get
			// smaller, so an element that was inside the print area stays inside.
			const scale = Math.min(
				obj.width ? width / obj.width : 1,
				obj.height ? height / obj.height : 1
			);
			obj.set({ scaleX: scale, scaleY: scale });
			obj.setPositionByOrigin(center, 'center', 'center');
			obj.setCoords();

			if (typeof done === 'function') done();
		}, null, 'anonymous');

		return true;
	}

	// The src the element is currently drawn from, whichever kind of element it is.
	currentSrc(){
		const obj = this.selectedObject;
		if (!obj) return null;
		// An SVG is a group tagged type 'image': it carries src but has no getSrc.
		return typeof obj.getSrc === 'function' ? obj.getSrc() : (obj.src || null);
	}

	applyImageEdit(data){
		if (!this.selectedObject || this.selectedObject.type !== 'image') return false;

		// SVGs sit on the canvas as groups tagged type 'image'. They have no pixel
		// buffer, so the filter pipeline does not exist on them - skip it and let
		// the rest of the edit (position, scale, angle) apply as usual.
		const supportsFilters = typeof this.selectedObject.applyFilters === 'function';

		if(supportsFilters){
			this.selectedObject.filters = [];
		}

		if(data.filter && supportsFilters){
			data.filter.forEach(f => {
				const filter = createFilter(f && f.type, f && f.value);
				if (filter) this.selectedObject.filters.push(filter);
			});
			resetFilterElement(this.selectedObject);
			this.selectedObject.applyFilters();
		}
		
		const canvas = this.canvasRef.current;
		canvas.renderAll();
		try { this.handleSave(); } catch (e) {}
		try { Logger('Image edited'); } catch (e) {}
		return true;
	}
}